import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { TruncatedCell } from '../components/common/TruncatedCell';
import { Autocomplete } from '../components/Autocomplete';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ClientLink } from '../components/links/EntityLinks';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit, Trash2, MoreVertical, MessageSquare, Search, ShieldCheck, RefreshCw, AlertTriangle, Clock, CheckCircle2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmation } from '../components/ConfirmationProvider';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDateTime } from '../lib/formatUtils';
import { useLookup } from '../lib/useLookup';

const TELECOM_FALLBACK = ['Jio', 'Airtel', 'BSNL', 'Vi', 'Other'];

const ComplianceBadge = ({ status, daysLeft }) => {
  if (status === 'overdue') return <Badge className="bg-red-500/10 text-red-500">Overdue ({Math.abs(daysLeft)}d)</Badge>;
  if (status === 'warning') return <Badge className="bg-amber-500/10 text-amber-500">Due in {daysLeft}d</Badge>;
  if (status === 'ok') return <Badge className="bg-emerald-500/10 text-emerald-500">OK ({daysLeft}d left)</Badge>;
  return <Badge variant="outline">Not Set</Badge>;
};

export const DLTPage = () => {
  const [accounts, setAccounts] = useState([]);
  const confirm = useConfirmation();
  const { options: telecomOptions } = useLookup('telecom_operator', TELECOM_FALLBACK);
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('dlt.create');
  const canEdit = hasPermission('dlt.edit');
  const canDelete = hasPermission('dlt.delete');
  const [templates, setTemplates] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAcctDialog, setIsAcctDialog] = useState(false);
  const [isTplDialog, setIsTplDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [acctForm, setAcctForm] = useState({ entity_name: '', entity_id: '', sender_ids: '', telecom: '', client_name: '', last_verification_date: '', last_renewal_date: '', status: 'active', notes: '' });
  const [tplForm, setTplForm] = useState({ account_id: '', template_name: '', template_id: '', type: 'transactional', content: '', status: 'pending', last_verification_date: '' });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [acctRes, tplRes, compRes] = await Promise.all([
        api.get('/dlt/accounts'),
        api.get('/dlt/templates'),
        api.get('/dlt/compliance')
      ]);
      if (acctRes.data.success) setAccounts(acctRes.data.data);
      if (tplRes.data.success) setTemplates(tplRes.data.data);
      if (compRes.data.success) setCompliance(compRes.data.data);
    } catch {}
  };

  // --- Account CRUD ---
  const handleAcctSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...acctForm, sender_ids: acctForm.sender_ids.split(',').map(s => s.trim()).filter(Boolean) };
    try {
      if (isEditing && selectedItem) {
        await api.put(`/dlt/accounts/${selectedItem.id}`, payload);
        toast.success('Account updated');
      } else {
        await api.post('/dlt/accounts', payload);
        toast.success('Account created');
      }
      fetchAll();
    } catch { toast.error('Operation failed'); }
    setIsAcctDialog(false); resetAcctForm();
  };

  const handleAcctDelete = async (acct) => {
    const confirmed = await confirm({ title: 'Delete DLT Account', description: `Are you sure you want to delete DLT account "${acct.entity_name}"? This action cannot be undone.` });
    if (!confirmed) return;
    try { await api.delete(`/dlt/accounts/${acct.id}`); toast.success('Account deleted'); fetchAll(); } catch { toast.error('Delete failed'); }
  };

  const handleAcctEdit = (acct) => {
    setSelectedItem(acct); setIsEditing(true);
    setAcctForm({ entity_name: acct.entity_name, entity_id: acct.entity_id, sender_ids: (acct.sender_ids || []).join(', '), telecom: acct.telecom || '', client_name: acct.client_name || '', last_verification_date: acct.last_verification_date?.split('T')[0] || '', last_renewal_date: acct.last_renewal_date?.split('T')[0] || '', status: acct.status, notes: acct.notes || '' });
    setIsAcctDialog(true);
  };

  const markVerified = async (acctId) => {
    try { await api.post(`/dlt/accounts/${acctId}/verify`); toast.success('Marked as verified'); fetchAll(); } catch { toast.error('Failed'); }
  };

  const markRenewed = async (acctId) => {
    try { await api.post(`/dlt/accounts/${acctId}/renew`); toast.success('Marked as renewed'); fetchAll(); } catch { toast.error('Failed'); }
  };

  const resetAcctForm = () => { setAcctForm({ entity_name: '', entity_id: '', sender_ids: '', telecom: '', client_name: '', last_verification_date: '', last_renewal_date: '', status: 'active', notes: '' }); setSelectedItem(null); setIsEditing(false); };

  // --- Template CRUD ---
  const handleTplSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedItem) {
        await api.put(`/dlt/templates/${selectedItem.id}`, tplForm);
        toast.success('Template updated');
      } else {
        await api.post('/dlt/templates', tplForm);
        toast.success('Template created');
      }
      fetchAll();
    } catch { toast.error('Operation failed'); }
    setIsTplDialog(false); resetTplForm();
  };

  const handleTplDelete = async (tpl) => {
    const confirmed = await confirm({ title: 'Delete Template', description: `Are you sure you want to delete template "${tpl.template_name}"? This action cannot be undone.` });
    if (!confirmed) return;
    try { await api.delete(`/dlt/templates/${tpl.id}`); toast.success('Deleted'); fetchAll(); } catch { toast.error('Failed'); }
  };

  const handleTplEdit = (tpl) => {
    setSelectedItem(tpl); setIsEditing(true);
    setTplForm({ account_id: tpl.account_id, template_name: tpl.template_name, template_id: tpl.template_id, type: tpl.type, content: tpl.content, status: tpl.status, last_verification_date: tpl.last_verification_date?.split('T')[0] || '' });
    setIsTplDialog(true);
  };

  const markTplVerified = async (tplId) => {
    try { await api.post(`/dlt/templates/${tplId}/verify`); toast.success('Template verified'); fetchAll(); } catch { toast.error('Failed'); }
  };

  const resetTplForm = () => { setTplForm({ account_id: '', template_name: '', template_id: '', type: 'transactional', content: '', status: 'pending', last_verification_date: '' }); setSelectedItem(null); setIsEditing(false); };

  const filteredAccounts = accounts.filter(a => !searchQuery || a.entity_name?.toLowerCase().includes(searchQuery.toLowerCase()) || a.entity_id?.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredTemplates = templates.filter(t => !searchQuery || t.template_name?.toLowerCase().includes(searchQuery.toLowerCase()) || t.content?.toLowerCase().includes(searchQuery.toLowerCase()));

  const compAlerts = compliance?.alerts || [];
  const hasComplianceIssues = compAlerts.length > 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dlt-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="text-2xl font-bold">DLT Management</h1><p className="text-muted-foreground">Manage DLT accounts, templates & compliance</p></div>
      </div>

      {/* Compliance Alert Banner */}
      {hasComplianceIssues && (
        <Card className="border-amber-500/50 bg-amber-500/5" data-testid="dlt-compliance-banner">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-amber-500">Compliance Attention Required</p>
                <ul className="mt-1 space-y-1">
                  {compAlerts.map((a, i) => (
                    <li key={i} className="text-sm text-muted-foreground">
                      <span className={a.days < 0 ? 'text-red-500 font-medium' : 'text-amber-500'}>{a.entity}</span>: {a.type.replace(/_/g, ' ')} — {a.days < 0 ? `${Math.abs(a.days)} days overdue` : `${a.days} days left`}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><Building2 className="h-5 w-5 text-primary" /></div><div><p className="text-2xl font-bold">{accounts.length}</p><p className="text-xs text-muted-foreground">DLT Accounts</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center"><MessageSquare className="h-5 w-5 text-blue-500" /></div><div><p className="text-2xl font-bold">{templates.length}</p><p className="text-xs text-muted-foreground">Templates</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-amber-500" /></div><div><p className="text-2xl font-bold">{(compliance?.verification_overdue || 0) + (compliance?.template_verification_overdue || 0)}</p><p className="text-xs text-muted-foreground">Verification Overdue</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-red-500/10 flex items-center justify-center"><Clock className="h-5 w-5 text-red-500" /></div><div><p className="text-2xl font-bold">{compliance?.renewal_overdue || 0}</p><p className="text-xs text-muted-foreground">Renewal Overdue</p></div></div></CardContent></Card>
      </div>

      <Card><CardContent className="py-4"><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search accounts & templates..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="dlt-search" /></div></CardContent></Card>

      <Tabs defaultValue="accounts">
        <TabsList>
          <TabsTrigger value="accounts" data-testid="dlt-tab-accounts">DLT Accounts ({accounts.length})</TabsTrigger>
          <TabsTrigger value="templates" data-testid="dlt-tab-templates">Templates ({templates.length})</TabsTrigger>
        </TabsList>

        {/* ACCOUNTS TAB */}
        <TabsContent value="accounts" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={isAcctDialog} onOpenChange={(o) => { setIsAcctDialog(o); if (!o) resetAcctForm(); }}>
              {canCreate && (<DialogTrigger asChild><Button data-testid="add-dlt-account-btn"><Plus className="h-4 w-4 mr-2" />Add Account</Button></DialogTrigger>)}
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>{isEditing ? 'Edit Account' : 'Add DLT Account'}</DialogTitle><DialogDescription>Enter DLT entity details with compliance dates</DialogDescription></DialogHeader>
                <form onSubmit={handleAcctSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Client <span className="text-red-500">*</span></Label>
                    <Autocomplete
                      value={acctForm.client_name}
                      onChange={v => setAcctForm({...acctForm, client_name: v})}
                      fetchOptions={async (q) => { const res = await api.get(`/clients?search=${q}&limit=10`); return res.data.success ? res.data.data.map(c => c.company_name || c.name) : []; }}
                      placeholder="Search clients..."
                      data-testid="dlt-acct-client"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Entity Name *</Label><Input value={acctForm.entity_name} onChange={e => setAcctForm({...acctForm, entity_name: e.target.value})} required /></div>
                    <div className="space-y-2"><Label>Entity ID *</Label><Input value={acctForm.entity_id} onChange={e => setAcctForm({...acctForm, entity_id: e.target.value})} placeholder="1101XXXXXXXXXX" required /></div>
                    <div className="space-y-2"><Label>Telecom</Label><Select value={acctForm.telecom} onValueChange={v => setAcctForm({...acctForm, telecom: v})}><SelectTrigger data-testid="dlt-telecom"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{telecomOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                    <div className="space-y-2"><Label>Sender IDs</Label><Input value={acctForm.sender_ids} onChange={e => setAcctForm({...acctForm, sender_ids: e.target.value})} placeholder="MYZOW, MYSOL" /></div>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                    <p className="text-xs font-medium text-amber-500 mb-2 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Compliance Dates</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label className="text-xs">Last Verification Date</Label><Input type="date" value={acctForm.last_verification_date} onChange={e => setAcctForm({...acctForm, last_verification_date: e.target.value})} data-testid="acct-last-verification" /></div>
                      <div className="space-y-2"><Label className="text-xs">Last Renewal Date</Label><Input type="date" value={acctForm.last_renewal_date} onChange={e => setAcctForm({...acctForm, last_renewal_date: e.target.value})} data-testid="acct-last-renewal" /></div>
                    </div>
                  </div>
                  <div className="space-y-2"><Label>Notes</Label><Input value={acctForm.notes} onChange={e => setAcctForm({...acctForm, notes: e.target.value})} /></div>
                  <DialogFooter><Button type="button" variant="outline" onClick={() => setIsAcctDialog(false)}>Cancel</Button><Button type="submit" data-testid="acct-submit-btn">{isEditing ? 'Update' : 'Add'}</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4">
            {filteredAccounts.map(acct => (
              <Card key={acct.id} className={`${acct.verification_status === 'overdue' || acct.renewal_status === 'overdue' ? 'border-red-500/30' : acct.verification_status === 'warning' || acct.renewal_status === 'warning' ? 'border-amber-500/30' : ''}`} data-testid={`dlt-account-${acct.id}`}>
                <CardContent className="p-5">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-base">{acct.entity_name}</h3>
                        <Badge variant="outline" className="text-[10px]">{acct.telecom}</Badge>
                        <Badge className={acct.status === 'active' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}>{acct.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground font-mono mb-2">{acct.entity_id}</p>
                      {acct.client_name && <p className="text-xs text-muted-foreground mb-2">Client: <span className="text-foreground"><ClientLink id={acct.client_id} name={acct.client_name} /></span></p>}
                      <div className="flex flex-wrap gap-1 mb-2">
                        {(acct.sender_ids || []).map(sid => <Badge key={sid} variant="outline" className="text-[10px]">{sid}</Badge>)}
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="text-center px-4 py-2 rounded-lg bg-muted/30 min-w-[130px]">
                        <p className="text-[10px] text-muted-foreground uppercase mb-1">90-Day Verification</p>
                        <ComplianceBadge status={acct.verification_status} daysLeft={acct.verification_days_left} />
                        <p className="text-[10px] text-muted-foreground mt-1">Last: {formatDate(acct.last_verification_date) || 'N/A'}</p>
                      </div>
                      <div className="text-center px-4 py-2 rounded-lg bg-muted/30 min-w-[130px]">
                        <p className="text-[10px] text-muted-foreground uppercase mb-1">Yearly Renewal</p>
                        <ComplianceBadge status={acct.renewal_status} daysLeft={acct.renewal_days_left} />
                        <p className="text-[10px] text-muted-foreground mt-1">Last: {formatDate(acct.last_renewal_date) || 'N/A'}</p>
                      </div>
                      <div className="flex flex-col gap-1">
                        {canEdit && (<Button size="sm" variant="outline" onClick={() => markVerified(acct.id)} className="text-xs" data-testid={`verify-acct-${acct.id}`}><ShieldCheck className="h-3.5 w-3.5 mr-1" />Mark Verified</Button>)}
                        {canEdit && (<Button size="sm" variant="outline" onClick={() => markRenewed(acct.id)} className="text-xs" data-testid={`renew-acct-${acct.id}`}><RefreshCw className="h-3.5 w-3.5 mr-1" />Mark Renewed</Button>)}
                        <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="text-xs"><MoreVertical className="h-3.5 w-3.5" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canEdit && (<DropdownMenuItem onClick={() => handleAcctEdit(acct)} data-testid={`acct-edit-${acct.id}`}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>)}
                          {canDelete && (<DropdownMenuItem onClick={() => handleAcctDelete(acct)} className="text-destructive" data-testid={`acct-delete-${acct.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>)}
                        </DropdownMenuContent></DropdownMenu>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {filteredAccounts.length === 0 && <p className="text-center text-muted-foreground py-8">No DLT accounts found</p>}
          </div>
        </TabsContent>

        {/* TEMPLATES TAB */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={isTplDialog} onOpenChange={(o) => { setIsTplDialog(o); if (!o) resetTplForm(); }}>
              {canCreate && (<DialogTrigger asChild><Button data-testid="add-dlt-template-btn"><Plus className="h-4 w-4 mr-2" />Add Template</Button></DialogTrigger>)}
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>{isEditing ? 'Edit Template' : 'Add DLT Template'}</DialogTitle><DialogDescription>Enter template details</DialogDescription></DialogHeader>
                <form onSubmit={handleTplSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Account *</Label>
                      <Select value={tplForm.account_id} onValueChange={v => setTplForm({...tplForm, account_id: v})}>
                        <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                        <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.entity_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>Template Name *</Label><Input value={tplForm.template_name} onChange={e => setTplForm({...tplForm, template_name: e.target.value})} required /></div>
                    <div className="space-y-2"><Label>Template ID</Label><Input value={tplForm.template_id} onChange={e => setTplForm({...tplForm, template_id: e.target.value})} placeholder="1107XXXX" /></div>
                    <div className="space-y-2"><Label>Type</Label><Select value={tplForm.type} onValueChange={v => setTplForm({...tplForm, type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{['transactional', 'promotional', 'service_implicit', 'service_explicit'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                    <div className="space-y-2 col-span-2"><Label>Content *</Label><Input value={tplForm.content} onChange={e => setTplForm({...tplForm, content: e.target.value})} placeholder="Use {#var#} for variables" required /></div>
                    <div className="space-y-2"><Label>Status</Label><Select value={tplForm.status} onValueChange={v => setTplForm({...tplForm, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{['approved', 'pending', 'rejected'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                    <div className="space-y-2"><Label>Last Verification</Label><Input type="date" value={tplForm.last_verification_date} onChange={e => setTplForm({...tplForm, last_verification_date: e.target.value})} /></div>
                  </div>
                  <DialogFooter><Button type="button" variant="outline" onClick={() => setIsTplDialog(false)}>Cancel</Button><Button type="submit" data-testid="tpl-submit-btn">{isEditing ? 'Update' : 'Add'}</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card><CardContent className="p-0">
            <Table data-testid="dlt-templates-table"><TableHeader><TableRow><TableHead>Template</TableHead><TableHead>ID</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead>Verification</TableHead><TableHead className="w-[100px]">Actions</TableHead></TableRow></TableHeader>
            <TableBody>{filteredTemplates.map(t => (
              <TableRow key={t.id}>
                <TableCell>
                  <div>
                    <p className="font-medium text-sm">{t.template_name}</p>
                    <TruncatedCell
                      text={t.content}
                      maxWidth={250}
                      testId={`dlt-content-${t.id}`}
                      className="text-xs text-muted-foreground"
                    />
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs">{t.template_id}</TableCell>
                <TableCell className="capitalize text-sm">{t.type}</TableCell>
                <TableCell><Badge className={t.status === 'approved' ? 'bg-emerald-500/10 text-emerald-500' : t.status === 'pending' ? 'bg-amber-500/10 text-amber-500' : 'bg-red-500/10 text-red-500'}>{t.status}</Badge></TableCell>
                <TableCell><ComplianceBadge status={t.verification_status} daysLeft={t.verification_days_left} /></TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {canEdit && (<Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => markTplVerified(t.id)} title="Mark Verified"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /></Button>)}
                    <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-3.5 w-3.5" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canEdit && (<DropdownMenuItem onClick={() => handleTplEdit(t)} data-testid={`tpl-edit-${t.id}`}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>)}
                      {canDelete && (<DropdownMenuItem onClick={() => handleTplDelete(t)} className="text-destructive" data-testid={`tpl-delete-${t.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>)}
                    </DropdownMenuContent></DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>))}</TableBody></Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
